import { OFFSET_MAX, OFFSET_MIN } from '../constants/wrap-offsets'

/** Matches a well-formed (fully or partially typed) number: optional leading '-', digits,
 *  optional single '.', more digits — same shape as the Kerning/Leading numeric-input pattern
 *  (`type-panel/helpers/spacing-input.ts`). Used only to distinguish the two error tiers in
 *  `offsetValidationMessage` below, not to block keystrokes — the field always shows exactly
 *  what was typed, letters included. */
const OFFSET_INPUT_PATTERN = /^-?\d*\.?\d*$/
const OFFSET_ERROR_MESSAGE = `Value: ${OFFSET_MIN}-${OFFSET_MAX}`
const OFFSET_INVALID_MESSAGE = 'Invalid value'

/** Parses a numeric candidate the same way the Kerning/Leading pattern does: a trailing '.' is
 *  treated as if a 0 followed it, so a fully-typed decimal (e.g. "1.") never spuriously fails to
 *  parse. Returns null for '' or a lone '-' — still-typing states with no number to validate yet,
 *  not errors. */
export function parseOffsetCandidate(raw: string): number | null {
  if (raw === '' || raw === '-') return null

  const n = parseFloat(raw.endsWith('.') ? `${raw}0` : raw)

  return Number.isFinite(n) ? n : null
}

/** Two-tier validation matching Kerning/Leading's own established pattern: a genuinely malformed
 *  string (letters, symbols, a second '.'/'-') gets a generic "Invalid value"; a well-formed but
 *  incomplete (lone '-') or out-of-range number gets the specific "Value: MIN-MAX" range message.
 *  Returns '' for a valid, in-range value (or for '' itself — nothing to validate yet). */
export function offsetValidationMessage(raw: string): string {
  if (raw === '') return ''
  if (!OFFSET_INPUT_PATTERN.test(raw)) return OFFSET_INVALID_MESSAGE

  const n = parseOffsetCandidate(raw)

  if (n === null || n < OFFSET_MIN || n > OFFSET_MAX) return OFFSET_ERROR_MESSAGE

  return ''
}

/** Validates one keystroke's text: the message to show ('' = valid) and, when the text is a
 *  valid, in-range number, the value to live-preview on the canvas (otherwise null). */
export function validateOffsetInput(raw: string): { message: string; liveValue: number | null } {
  const message = offsetValidationMessage(raw)

  return { message, liveValue: message === '' ? parseOffsetCandidate(raw) : null }
}
